import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { buildsApi } from '../../../api/builds';
import type { PdbFunction } from '../../../api/builds';
import {
  Search,
  Code,
  ChevronDown,
  Check,
  X,
  Loader,
  Eye,
  Shield,
  Zap,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  CheckSquare,
  Square,
  Minus,
  Settings2,
  Layers,
} from 'lucide-react';
import { clsx } from 'clsx';

// Protection types
export type ProtectionType = 'virtualization' | 'mutation' | 'ultra';

export interface SelectedFunction {
  rva: number;
  name: string;
  demangledName?: string;
  size?: number;
  protectionType: ProtectionType;
  complexity?: number; // Only for virtualization and ultra (1-100)
}

interface FunctionsTabProps {
  uploadId: string;
  hasPdb: boolean;
  onFunctionsChange: (functions: SelectedFunction[]) => void;
  onOpenDisassembly: (func: PdbFunction) => void;
  selectedFunctions: SelectedFunction[];
}

const PROTECTION_OPTIONS: { value: ProtectionType; label: string; icon: React.ReactNode; color: string; hasComplexity: boolean }[] = [
  {
    value: 'virtualization',
    label: 'Virtualization',
    icon: <Shield className="w-4 h-4" />,
    color: 'cyan',
    hasComplexity: true,
  },
  {
    value: 'mutation',
    label: 'Mutation',
    icon: <Zap className="w-4 h-4" />,
    color: 'purple',
    hasComplexity: false,
  },
  {
    value: 'ultra',
    label: 'Ultra',
    icon: <Sparkles className="w-4 h-4" />,
    color: 'pink',
    hasComplexity: true,
  },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 500, -1]; // -1 means "All"
const DEFAULT_COMPLEXITY = 50;

export function FunctionsTab({
  uploadId,
  hasPdb,
  onFunctionsChange,
  onOpenDisassembly,
  selectedFunctions,
}: FunctionsTabProps) {
  const [functions, setFunctions] = useState<PdbFunction[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState<'name' | 'address'>('address');
  const [limit, setLimit] = useState(50);
  
  // Multi-select state
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [checkedFunctions, setCheckedFunctions] = useState<Set<number>>(new Set());
  
  // Dropdown states
  const [expandedFunction, setExpandedFunction] = useState<number | null>(null);
  const [selectAllDropdownOpen, setSelectAllDropdownOpen] = useState(false);
  const [pageSizeDropdownOpen, setPageSizeDropdownOpen] = useState(false);
  const [batchProtectionOpen, setBatchProtectionOpen] = useState(false);
  const [batchComplexity, setBatchComplexity] = useState(DEFAULT_COMPLEXITY);
  
  // Complexity for dropdown selection
  const [dropdownComplexity, setDropdownComplexity] = useState(DEFAULT_COMPLEXITY);
  
  // Complexity editing in selected functions panel
  const [editingComplexity, setEditingComplexity] = useState<number | null>(null);
  const [complexityValue, setComplexityValue] = useState(DEFAULT_COMPLEXITY);
  
  // Dropdown position for portal
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);

  // Refs for click-outside handling
  const selectAllRef = useRef<HTMLDivElement>(null);
  const pageSizeRef = useRef<HTMLDivElement>(null);
  const batchProtectionRef = useRef<HTMLDivElement>(null);

  // Create a map for quick lookup of selected functions
  const selectedMap = useMemo(() => {
    const map = new Map<number, SelectedFunction>();
    selectedFunctions.forEach(f => map.set(f.rva, f));
    return map;
  }, [selectedFunctions]);

  // Filter out already selected functions from the available list
  const availableFunctions = useMemo(() => {
    return functions.filter(f => !selectedMap.has(f.rva));
  }, [functions, selectedMap]);

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Close dropdowns on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (selectAllRef.current && !selectAllRef.current.contains(event.target as Node)) {
        setSelectAllDropdownOpen(false);
      }
      if (pageSizeRef.current && !pageSizeRef.current.contains(event.target as Node)) {
        setPageSizeDropdownOpen(false);
      }
      if (batchProtectionRef.current && !batchProtectionRef.current.contains(event.target as Node)) {
        setBatchProtectionOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch functions - when searching or showing all, use higher limit
  const fetchFunctions = useCallback(async () => {
    if (!hasPdb) return;

    setLoading(true);
    setError(null);

    try {
      // When searching or limit is -1 (all), use a very high limit
      const effectiveLimit = debouncedSearch || limit === -1 ? 99999 : limit;
      
      const result = await buildsApi.getFunctions(uploadId, {
        search: debouncedSearch || undefined,
        page: (debouncedSearch || limit === -1) ? 1 : page,
        limit: effectiveLimit,
        sortBy,
      });
      setFunctions(result.data);
      setTotalPages(result.pagination.totalPages);
      setTotal(result.pagination.total);
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { error?: string } } };
      setError(axiosError.response?.data?.error || 'Failed to load functions');
    } finally {
      setLoading(false);
    }
  }, [uploadId, hasPdb, debouncedSearch, page, limit, sortBy]);

  useEffect(() => {
    fetchFunctions();
  }, [fetchFunctions]);

  // Handle function selection with protection type and complexity
  const selectFunction = (func: PdbFunction, protectionType: ProtectionType, complexity?: number) => {
    const existing = selectedMap.get(func.rva);
    const option = PROTECTION_OPTIONS.find(o => o.value === protectionType);
    
    if (existing) {
      // Update existing
      onFunctionsChange(
        selectedFunctions.map(f =>
          f.rva === func.rva 
            ? { 
                ...f, 
                protectionType,
                complexity: option?.hasComplexity ? (complexity ?? f.complexity ?? DEFAULT_COMPLEXITY) : undefined
              } 
            : f
        )
      );
    } else {
      // Add new
      onFunctionsChange([
        ...selectedFunctions,
        {
          rva: func.rva,
          name: func.name,
          demangledName: func.demangledName,
          size: func.size,
          protectionType,
          complexity: option?.hasComplexity ? (complexity ?? DEFAULT_COMPLEXITY) : undefined,
        },
      ]);
    }
    
    setExpandedFunction(null);
  };

  // Open protection dropdown with position tracking
  const openProtectionDropdown = (rva: number, event: React.MouseEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + 4,
      left: Math.min(rect.left, window.innerWidth - 200), // Ensure dropdown doesn't go off-screen
    });
    setExpandedFunction(rva);
  };

  // Close protection dropdown
  const closeProtectionDropdown = () => {
    setExpandedFunction(null);
    setDropdownPosition(null);
    setDropdownComplexity(DEFAULT_COMPLEXITY); // Reset complexity on close
  };

  // Update complexity for a selected function
  const updateComplexity = (rva: number, complexity: number) => {
    onFunctionsChange(
      selectedFunctions.map(f =>
        f.rva === rva ? { ...f, complexity } : f
      )
    );
    setEditingComplexity(null);
  };

  // Toggle checkbox in multi-select mode
  const toggleChecked = (rva: number) => {
    const newChecked = new Set(checkedFunctions);
    if (newChecked.has(rva)) {
      newChecked.delete(rva);
    } else {
      newChecked.add(rva);
    }
    setCheckedFunctions(newChecked);
  };

  // Check/uncheck all visible (only unselected functions)
  const toggleAllVisible = () => {
    const visibleRvas = availableFunctions.map(f => f.rva);
    const allChecked = visibleRvas.every(rva => checkedFunctions.has(rva));
    
    if (allChecked) {
      // Uncheck all visible
      const newChecked = new Set(checkedFunctions);
      visibleRvas.forEach(rva => newChecked.delete(rva));
      setCheckedFunctions(newChecked);
    } else {
      // Check all visible
      const newChecked = new Set(checkedFunctions);
      visibleRvas.forEach(rva => newChecked.add(rva));
      setCheckedFunctions(newChecked);
    }
  };

  // Apply batch protection to checked functions
  const applyBatchProtection = (protectionType: ProtectionType, complexity?: number) => {
    const checkedArray = Array.from(checkedFunctions);
    const option = PROTECTION_OPTIONS.find(o => o.value === protectionType);
    const effectiveComplexity = option?.hasComplexity ? (complexity ?? batchComplexity) : undefined;
    
    // Find function details for checked items (only from available functions)
    const newSelections: SelectedFunction[] = [...selectedFunctions];
    
    for (const rva of checkedArray) {
      const func = availableFunctions.find(f => f.rva === rva);
      if (!func) continue;
      
      // Add new (all checked functions are from availableFunctions, so they're not selected yet)
      newSelections.push({
        rva: func.rva,
        name: func.name,
        demangledName: func.demangledName,
        size: func.size,
        protectionType,
        complexity: effectiveComplexity,
      });
    }
    
    onFunctionsChange(newSelections);
    setCheckedFunctions(new Set());
    setBatchProtectionOpen(false);
    setMultiSelectMode(false);
  };

  // Select ALL functions (fetch all and select)
  const selectAllFunctions = async (protectionType: ProtectionType, complexity?: number) => {
    if (!hasPdb) return;
    
    setLoadingAll(true);
    setSelectAllDropdownOpen(false);
    
    try {
      // Fetch ALL functions (use very high limit)
      const result = await buildsApi.getFunctions(uploadId, {
        limit: 99999,
        sortBy: 'address',
      });
      
      const option = PROTECTION_OPTIONS.find(o => o.value === protectionType);
      const effectiveComplexity = option?.hasComplexity ? (complexity ?? DEFAULT_COMPLEXITY) : undefined;
      
      const allSelections: SelectedFunction[] = result.data.map(func => ({
        rva: func.rva,
        name: func.name,
        demangledName: func.demangledName,
        size: func.size,
        protectionType,
        complexity: effectiveComplexity,
      }));
      
      onFunctionsChange(allSelections);
    } catch (err) {
      console.error('Failed to select all functions:', err);
    } finally {
      setLoadingAll(false);
    }
  };

  // Select all visible functions (only unselected ones)
  const selectAllVisible = (protectionType: ProtectionType, complexity?: number) => {
    const option = PROTECTION_OPTIONS.find(o => o.value === protectionType);
    const effectiveComplexity = option?.hasComplexity ? (complexity ?? DEFAULT_COMPLEXITY) : undefined;
    
    const newSelections: SelectedFunction[] = [...selectedFunctions];
    
    for (const func of availableFunctions) {
      newSelections.push({
        rva: func.rva,
        name: func.name,
        demangledName: func.demangledName,
        size: func.size,
        protectionType,
        complexity: effectiveComplexity,
      });
    }
    
    onFunctionsChange(newSelections);
    setSelectAllDropdownOpen(false);
  };

  // Deselect all functions
  const deselectAll = () => {
    onFunctionsChange([]);
  };

  // Format hex address
  const formatHex = (value: number): string => {
    return '0x' + value.toString(16).toUpperCase().padStart(8, '0');
  };

  // Format size
  const formatSize = (bytes?: number): string => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    return `${(bytes / 1024).toFixed(1)} KB`;
  };

  // Highlight search matches
  const highlightMatch = (text: string): React.ReactNode => {
    if (!debouncedSearch.trim()) return text;
    
    const escapedQuery = debouncedSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedQuery})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, i) => {
      const isMatch = part.toLowerCase() === debouncedSearch.toLowerCase();
      return isMatch ? (
        <mark key={i} className="bg-cyan-500/40 text-cyan-200 rounded px-0.5 font-semibold">{part}</mark>
      ) : (
        <span key={i}>{part}</span>
      );
    });
  };

  // Check if all visible (available) functions are checked
  const allVisibleChecked = availableFunctions.length > 0 && availableFunctions.every(f => checkedFunctions.has(f.rva));
  const someVisibleChecked = availableFunctions.some(f => checkedFunctions.has(f.rva));

  if (!hasPdb) {
    return (
      <div className="text-center py-16">
        <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-slate-800/50 flex items-center justify-center border border-slate-700/50">
          <Code className="w-10 h-10 text-slate-500" />
        </div>
        <h3 className="text-lg font-semibold text-white mb-2">No PDB File</h3>
        <p className="text-slate-500 max-w-md mx-auto">
          Upload a PDB file to view and select functions for protection.
          The PDB file contains debug symbols that help identify functions in your binary.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <div>
            <p className="text-red-400 font-medium">Failed to Load Functions</p>
            <p className="text-red-400/70 text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with search and stats */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              placeholder="Search functions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-800/50 border border-slate-700/50 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/25"
            />
            {debouncedSearch && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">
                {total} results
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            {/* Sort toggle */}
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-500 hidden sm:inline">Sort:</span>
              <button
                onClick={() => setSortBy(sortBy === 'name' ? 'address' : 'name')}
                className="px-3 py-1.5 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-600 transition-colors"
              >
                {sortBy === 'name' ? 'Name' : 'Address'}
              </button>
            </div>
            
            {/* Selection count */}
            <div className="px-3 py-1.5 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm font-medium">
              {selectedFunctions.length} selected
            </div>
          </div>
        </div>

        {/* Action buttons row */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Select All Dropdown */}
          <div className="relative" ref={selectAllRef}>
            <button
              onClick={() => setSelectAllDropdownOpen(!selectAllDropdownOpen)}
              disabled={loadingAll}
              className={clsx(
                'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                loadingAll
                  ? 'bg-slate-800/30 text-slate-600 cursor-wait'
                  : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20'
              )}
            >
              {loadingAll ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Layers className="w-4 h-4" />
              )}
              Select All
              <ChevronDown className={clsx('w-3 h-3 transition-transform', selectAllDropdownOpen && 'rotate-180')} />
            </button>

            {selectAllDropdownOpen && (
              <div className="absolute top-full left-0 mt-1 z-30 w-64 rounded-xl border border-slate-700/50 bg-slate-900 backdrop-blur-xl shadow-2xl shadow-black/50 overflow-hidden">
                <div className="px-3 py-2 border-b border-slate-700/50">
                  <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Select All Functions</p>
                </div>
                {PROTECTION_OPTIONS.map((option) => (
                  <button
                    key={`all-${option.value}`}
                    onClick={() => selectAllFunctions(option.value)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors text-left text-slate-300 hover:bg-slate-800/50"
                  >
                    <span className={clsx(
                      option.color === 'cyan' && 'text-cyan-400',
                      option.color === 'purple' && 'text-purple-400',
                      option.color === 'pink' && 'text-pink-400'
                    )}>
                      {option.icon}
                    </span>
                    All with {option.label}
                    {option.hasComplexity && <span className="text-xs text-slate-500 ml-auto">+complexity</span>}
                  </button>
                ))}
                <div className="border-t border-slate-700/50">
                  <div className="px-3 py-2">
                    <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Select Visible Only</p>
                  </div>
                  {PROTECTION_OPTIONS.map((option) => (
                    <button
                      key={`visible-${option.value}`}
                      onClick={() => selectAllVisible(option.value)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors text-left text-slate-300 hover:bg-slate-800/50"
                    >
                      <span className={clsx(
                        option.color === 'cyan' && 'text-cyan-400',
                        option.color === 'purple' && 'text-purple-400',
                        option.color === 'pink' && 'text-pink-400'
                      )}>
                        {option.icon}
                      </span>
                      Visible with {option.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Multi-select toggle */}
          <button
            onClick={() => {
              setMultiSelectMode(!multiSelectMode);
              if (multiSelectMode) {
                setCheckedFunctions(new Set());
              }
            }}
            className={clsx(
              'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
              multiSelectMode
                ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                : 'bg-slate-800/50 text-slate-300 border border-slate-700/50 hover:text-white hover:border-slate-600'
            )}
          >
            <CheckSquare className="w-4 h-4" />
            Multi-Select
          </button>

          {/* Batch apply protection (shown when multi-select is active and items are checked) */}
          {multiSelectMode && checkedFunctions.size > 0 && (
            <div className="relative" ref={batchProtectionRef}>
              <button
                onClick={() => setBatchProtectionOpen(!batchProtectionOpen)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-white border border-cyan-500/30 hover:from-cyan-500/30 hover:to-purple-500/30 transition-colors"
              >
                <Shield className="w-4 h-4" />
                Apply to {checkedFunctions.size} selected
                <ChevronDown className={clsx('w-3 h-3 transition-transform', batchProtectionOpen && 'rotate-180')} />
              </button>

              {batchProtectionOpen && (
                <div className="absolute top-full left-0 mt-1 z-30 w-72 rounded-xl border border-slate-700/50 bg-slate-900 backdrop-blur-xl shadow-2xl shadow-black/50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-700/50">
                    <p className="text-sm text-white font-medium">Apply Protection</p>
                    <p className="text-xs text-slate-400 mt-1">Set protection for {checkedFunctions.size} checked functions</p>
                  </div>
                  
                  {/* Complexity slider (for virtualization/ultra) */}
                  <div className="px-4 py-3 border-b border-slate-700/50">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs text-slate-400">Complexity (VM/Ultra)</label>
                      <span className="text-xs text-cyan-400 font-mono">{batchComplexity}</span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="100"
                      value={batchComplexity}
                      onChange={(e) => setBatchComplexity(Number(e.target.value))}
                      className="w-full h-1.5 bg-slate-700 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-cyan-400"
                    />
                  </div>
                  
                  {PROTECTION_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => applyBatchProtection(option.value, option.hasComplexity ? batchComplexity : undefined)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors text-left text-slate-300 hover:bg-slate-800/50"
                    >
                      <span className={clsx(
                        option.color === 'cyan' && 'text-cyan-400',
                        option.color === 'purple' && 'text-purple-400',
                        option.color === 'pink' && 'text-pink-400'
                      )}>
                        {option.icon}
                      </span>
                      <div>
                        <p className="font-medium">{option.label}</p>
                        {option.hasComplexity && (
                          <p className="text-xs text-slate-500">Complexity: {batchComplexity}</p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Clear selection */}
          {selectedFunctions.length > 0 && (
            <button
              onClick={deselectAll}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 transition-colors"
            >
              <X className="w-4 h-4" />
              Clear All ({selectedFunctions.length})
            </button>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Page size dropdown */}
          <div className="relative" ref={pageSizeRef}>
            <button
              onClick={() => setPageSizeDropdownOpen(!pageSizeDropdownOpen)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-800/50 text-slate-300 border border-slate-700/50 hover:text-white hover:border-slate-600 transition-colors"
            >
              <Settings2 className="w-4 h-4" />
              {limit === -1 ? 'All' : `${limit}/page`}
              <ChevronDown className={clsx('w-3 h-3 transition-transform', pageSizeDropdownOpen && 'rotate-180')} />
            </button>

            {pageSizeDropdownOpen && (
              <div className="absolute top-full right-0 mt-1 z-30 w-36 rounded-xl border border-slate-700/50 bg-slate-900 backdrop-blur-xl shadow-2xl shadow-black/50 overflow-hidden">
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <button
                    key={size}
                    onClick={() => {
                      setLimit(size);
                      setPage(1);
                      setPageSizeDropdownOpen(false);
                    }}
                    className={clsx(
                      'w-full flex items-center justify-between gap-2 px-4 py-2.5 text-sm transition-colors text-left',
                      limit === size
                        ? 'bg-slate-800 text-white'
                        : 'text-slate-300 hover:bg-slate-800/50'
                    )}
                  >
                    {size === -1 ? 'Show All' : `${size} per page`}
                    {limit === size && <Check className="w-4 h-4 text-cyan-400" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Functions list */}
      <div className="rounded-xl border border-slate-700/50 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="w-6 h-6 text-cyan-400 animate-spin" />
            <span className="ml-3 text-slate-400">Loading functions...</span>
          </div>
        ) : availableFunctions.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            {functions.length > 0 
              ? 'All functions have been configured. See selected functions below.'
              : debouncedSearch 
                ? 'No matching functions found' 
                : 'No functions available'}
          </div>
        ) : (
          <>
            {/* Table header */}
            <div className="flex items-center bg-slate-800/30 border-b border-slate-700/50 text-xs text-slate-400 font-medium uppercase tracking-wider pr-2">
              {multiSelectMode && (
                <div className="w-12 flex-shrink-0 px-3 py-3 text-center">
                  <button
                    onClick={toggleAllVisible}
                    className="p-1 rounded hover:bg-slate-700/50 transition-colors"
                  >
                    {allVisibleChecked ? (
                      <CheckSquare className="w-4 h-4 text-cyan-400" />
                    ) : someVisibleChecked ? (
                      <Minus className="w-4 h-4 text-cyan-400" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                  </button>
                </div>
              )}
              <div className="flex-1 min-w-0 px-4 py-3 text-left">Function Name</div>
              <div className="w-28 flex-shrink-0 px-3 py-3 text-right">Address</div>
              <div className="w-16 flex-shrink-0 px-3 py-3 text-right">Size</div>
              <div className="w-32 flex-shrink-0 px-3 py-3 text-center">Protection</div>
              <div className="w-20 flex-shrink-0 px-3 py-3 text-center">Actions</div>
            </div>

            {/* Scrollable rows - only show unselected functions */}
            <div className="max-h-[500px] overflow-y-auto divide-y divide-slate-800/50">
              {availableFunctions.map((func) => {
                const isExpanded = expandedFunction === func.rva;
                const isChecked = checkedFunctions.has(func.rva);

                return (
                  <div
                    key={func.rva}
                    className={clsx(
                      'flex items-center transition-colors',
                      isChecked
                        ? 'bg-slate-800/50'
                        : 'hover:bg-slate-800/30'
                    )}
                  >
                    {/* Multi-select checkbox */}
                    {multiSelectMode && (
                      <div className="w-12 flex-shrink-0 px-3 py-3 text-center">
                        <button
                          onClick={() => toggleChecked(func.rva)}
                          className={clsx(
                            'w-5 h-5 rounded flex items-center justify-center transition-all mx-auto',
                            isChecked
                              ? 'bg-cyan-500 text-white'
                              : 'border border-slate-600 hover:border-slate-500'
                          )}
                        >
                          {isChecked && <Check className="w-3 h-3" />}
                        </button>
                      </div>
                    )}

                    {/* Function name */}
                    <div className="flex-1 min-w-0 px-4 py-3">
                      <p className="font-mono text-sm text-white truncate" title={func.name}>
                        {highlightMatch(func.demangledName || func.name)}
                      </p>
                      {func.demangledName && func.demangledName !== func.name && (
                        <p className="font-mono text-xs text-slate-500 truncate" title={func.name}>
                          {func.name}
                        </p>
                      )}
                    </div>

                    {/* Address */}
                    <div className="w-28 flex-shrink-0 px-3 py-3 text-right">
                      <span className="font-mono text-sm text-cyan-400">
                        {formatHex(func.rva)}
                      </span>
                    </div>

                    {/* Size */}
                    <div className="w-16 flex-shrink-0 px-3 py-3 text-right">
                      <span className="font-mono text-sm text-slate-400">
                        {formatSize(func.size)}
                      </span>
                    </div>

                    {/* Protection dropdown - all functions here are unselected */}
                    <div className="w-32 flex-shrink-0 px-3 py-3">
                      <div className="flex items-center justify-center">
                        <button
                          onClick={(e) => isExpanded ? closeProtectionDropdown() : openProtectionDropdown(func.rva, e)}
                          className="px-2 py-1 rounded-lg text-xs text-slate-500 hover:text-slate-300 hover:bg-slate-800/50 transition-colors"
                        >
                          + Add
                        </button>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="w-20 flex-shrink-0 px-3 py-3 text-center">
                      <button
                        onClick={() => onOpenDisassembly(func)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800/50 transition-colors"
                        title="View disassembly"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Pagination (only when not searching and not showing all) */}
      {!debouncedSearch && limit !== -1 && totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            Showing {availableFunctions.length} of {total - selectedFunctions.length} available
            {selectedFunctions.length > 0 && (
              <span className="text-cyan-400 ml-1">({selectedFunctions.length} configured)</span>
            )}
          </p>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className={clsx(
                'p-2 rounded-lg transition-colors',
                page <= 1
                  ? 'text-slate-600 cursor-not-allowed'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
              )}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            
            <span className="px-4 py-2 text-sm text-slate-400">
              Page {page} of {totalPages}
            </span>
            
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className={clsx(
                'p-2 rounded-lg transition-colors',
                page >= totalPages
                  ? 'text-slate-600 cursor-not-allowed'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
              )}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Selected functions summary */}
      {selectedFunctions.length > 0 && (
        <div className="p-4 rounded-xl bg-slate-800/30 border border-slate-700/50">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-white">Selected Functions ({selectedFunctions.length})</h4>
            <button
              onClick={() => onFunctionsChange([])}
              className="text-xs text-slate-500 hover:text-red-400 transition-colors"
            >
              Clear all
            </button>
          </div>
          <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
            {selectedFunctions.slice(0, 20).map((func) => {
              const isEditingThis = editingComplexity === func.rva;
              const hasComplexity = func.protectionType === 'virtualization' || func.protectionType === 'ultra';
              
              return (
                <div
                  key={func.rva}
                  className={clsx(
                    'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm',
                    func.protectionType === 'virtualization' && 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20',
                    func.protectionType === 'mutation' && 'bg-purple-500/10 text-purple-400 border border-purple-500/20',
                    func.protectionType === 'ultra' && 'bg-pink-500/10 text-pink-400 border border-pink-500/20'
                  )}
                >
                  <span className="font-mono truncate max-w-[150px]">{func.demangledName || func.name}</span>
                  {hasComplexity && (
                    isEditingThis ? (
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={complexityValue}
                        onChange={(e) => setComplexityValue(Math.min(100, Math.max(1, Number(e.target.value))))}
                        onBlur={() => updateComplexity(func.rva, complexityValue)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') updateComplexity(func.rva, complexityValue);
                          else if (e.key === 'Escape') setEditingComplexity(null);
                        }}
                        autoFocus
                        className="w-10 px-1 py-0.5 rounded bg-slate-800 border border-cyan-500/50 text-white text-xs font-mono text-center focus:outline-none"
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <button
                        onClick={() => {
                          setComplexityValue(func.complexity ?? DEFAULT_COMPLEXITY);
                          setEditingComplexity(func.rva);
                        }}
                        className="px-1.5 py-0.5 rounded bg-slate-800/50 text-xs hover:bg-slate-700/50 transition-colors font-mono"
                        title="Click to edit complexity"
                      >
                        C:{func.complexity ?? DEFAULT_COMPLEXITY}
                      </button>
                    )
                  )}
                  <button
                    onClick={() => onFunctionsChange(selectedFunctions.filter(f => f.rva !== func.rva))}
                    className="opacity-50 hover:opacity-100"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
            {selectedFunctions.length > 20 && (
              <div className="px-3 py-1.5 rounded-lg bg-slate-700/50 text-slate-400 text-sm">
                +{selectedFunctions.length - 20} more
              </div>
            )}
          </div>
        </div>
      )}

      {/* Protection dropdown portal - rendered outside scroll container */}
      {expandedFunction !== null && dropdownPosition && createPortal(
        <>
          {/* Backdrop to close dropdown on click outside */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={closeProtectionDropdown}
          />
          {/* Dropdown menu */}
          <div 
            className="fixed z-50 w-64 rounded-xl border border-slate-700/50 bg-slate-900 backdrop-blur-xl shadow-2xl shadow-black/50 overflow-hidden"
            style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
          >
            {/* Complexity slider for VM/Ultra */}
            <div className="px-4 py-3 border-b border-slate-700/50">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-slate-400">Complexity (VM/Ultra)</label>
                <span className="text-xs text-cyan-400 font-mono">{dropdownComplexity}</span>
              </div>
              <input
                type="range"
                min="1"
                max="100"
                value={dropdownComplexity}
                onChange={(e) => setDropdownComplexity(Number(e.target.value))}
                className="w-full h-1.5 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
              />
            </div>
            
            {PROTECTION_OPTIONS.map((option) => {
              const func = availableFunctions.find(f => f.rva === expandedFunction);
              
              return (
                <button
                  key={option.value}
                  onClick={() => {
                    if (func) {
                      selectFunction(func, option.value, option.hasComplexity ? dropdownComplexity : undefined);
                    }
                    closeProtectionDropdown();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors text-left text-slate-300 hover:bg-slate-800/50"
                >
                  <span className={clsx(
                    option.color === 'cyan' && 'text-cyan-400',
                    option.color === 'purple' && 'text-purple-400',
                    option.color === 'pink' && 'text-pink-400'
                  )}>
                    {option.icon}
                  </span>
                  <span className="flex-1">{option.label}</span>
                  {option.hasComplexity && (
                    <span className="text-xs text-slate-500">C:{dropdownComplexity}</span>
                  )}
                </button>
              );
            })}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
